import { useParams } from "react-router-dom";
import {
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  Loader2,
  Database,
  Calculator,
  Table2,
  AlertCircle,
  BarChart3,
  Shield,
  BookOpen,
  Factory,
  Share2,
} from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";
import { useSharedConversation, type SharedConversation as SharedConvType } from "@/hooks/use-sharing";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";

const CHART_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ef4444", "#06b6d4", "#f97316", "#ec4899",
];

interface ChartSpec {
  chart_type: "bar" | "line" | "pie" | "scatter";
  title: string;
  data: Record<string, unknown>[];
  x_key: string;
  y_keys: string[];
  x_label?: string;
  y_label?: string;
}

// --- Markdown components (same as DataAnalyst) ---

const markdownComponents = {
  table: ({ children, ...props }: React.ComponentProps<"table">) => (
    <div className="my-2 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs" {...props}>{children}</table>
    </div>
  ),
  thead: ({ children, ...props }: React.ComponentProps<"thead">) => (
    <thead className="bg-muted/50" {...props}>{children}</thead>
  ),
  th: ({ children, ...props }: React.ComponentProps<"th">) => (
    <th className="px-3 py-1.5 text-left font-medium text-muted-foreground" {...props}>{children}</th>
  ),
  td: ({ children, ...props }: React.ComponentProps<"td">) => (
    <td className="border-t border-border/50 px-3 py-1.5" {...props}>{children}</td>
  ),
  code: ({ children, className, ...props }: React.ComponentProps<"code">) => {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return (
        <code className="block rounded-md bg-muted p-3 font-mono text-xs overflow-x-auto" {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: React.ComponentProps<"pre">) => (
    <pre className="my-2" {...props}>{children}</pre>
  ),
  h1: ({ children, ...props }: React.ComponentProps<"h1">) => (
    <h3 className="mt-3 mb-1 text-sm font-semibold" {...props}>{children}</h3>
  ),
  h2: ({ children, ...props }: React.ComponentProps<"h2">) => (
    <h4 className="mt-3 mb-1 text-sm font-semibold" {...props}>{children}</h4>
  ),
  h3: ({ children, ...props }: React.ComponentProps<"h3">) => (
    <h5 className="mt-2 mb-1 text-sm font-medium" {...props}>{children}</h5>
  ),
  ul: ({ children, ...props }: React.ComponentProps<"ul">) => (
    <ul className="my-1 ml-6 list-disc space-y-0.5" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.ComponentProps<"ol">) => (
    <ol className="my-1 ml-6 list-decimal space-y-0.5" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: React.ComponentProps<"li">) => (
    <li className="text-sm" {...props}>{children}</li>
  ),
  p: ({ children, ...props }: React.ComponentProps<"p">) => (
    <p className="my-1.5 leading-relaxed" {...props}>{children}</p>
  ),
  blockquote: ({ children, ...props }: React.ComponentProps<"blockquote">) => (
    <blockquote className="my-2 border-l-2 border-primary/40 pl-3 text-muted-foreground italic" {...props}>
      {children}
    </blockquote>
  ),
  strong: ({ children, ...props }: React.ComponentProps<"strong">) => (
    <strong className="font-semibold" {...props}>{children}</strong>
  ),
  a: ({ children, href, ...props }: React.ComponentProps<"a">) => (
    <a className="text-primary underline underline-offset-2" href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
  hr: (props: React.ComponentProps<"hr">) => (
    <hr className="my-3 border-border/50" {...props} />
  ),
};

// --- Chart renderer (read-only, no pin) ---

function ChartBlock({ spec }: { spec: ChartSpec }) {
  const { chart_type, title, data, x_key, y_keys, x_label, y_label } = spec;

  if (!data?.length || !x_key || !y_keys?.length) {
    return <p className="text-xs text-muted-foreground italic">No chart data</p>;
  }

  const commonProps = {
    data,
    margin: { top: 5, right: 20, bottom: 5, left: 10 },
  };

  return (
    <div className="my-3 rounded-lg border border-border bg-card p-4">
      <p className="mb-3 text-xs font-medium text-muted-foreground">{title}</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {chart_type === "bar" ? (
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey={x_key} tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={x_label ? { value: x_label, position: "insideBottom", offset: -5, fontSize: 11 } : undefined} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={y_label ? { value: y_label, angle: -90, position: "insideLeft", fontSize: 11 } : undefined} />
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
              {y_keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {y_keys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          ) : chart_type === "line" ? (
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey={x_key} tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={x_label ? { value: x_label, position: "insideBottom", offset: -5, fontSize: 11 } : undefined} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={y_label ? { value: y_label, angle: -90, position: "insideLeft", fontSize: 11 } : undefined} />
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
              {y_keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {y_keys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          ) : chart_type === "pie" ? (
            <PieChart>
              <Pie data={data} dataKey={y_keys[0]} nameKey={x_key} cx="50%" cy="50%" outerRadius={90} label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                {data.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
            </PieChart>
          ) : (
            <ScatterChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey={x_key} type="number" tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={x_label ? { value: x_label, position: "insideBottom", offset: -5, fontSize: 11 } : undefined} />
              <YAxis dataKey={y_keys[0]} type="number" tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={y_label ? { value: y_label, angle: -90, position: "insideLeft", fontSize: 11 } : undefined} />
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
              <Scatter data={data} fill={CHART_COLORS[0]} />
            </ScatterChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// --- Tool call helpers ---

function toolIcon(tool: string) {
  switch (tool) {
    case "run_sql": return <Database className="h-3.5 w-3.5" />;
    case "calculate": case "calculate_process_stats": return <Calculator className="h-3.5 w-3.5" />;
    case "get_table_schema": case "list_tables": return <Table2 className="h-3.5 w-3.5" />;
    case "create_chart": return <BarChart3 className="h-3.5 w-3.5" />;
    case "get_spec_limits": return <Shield className="h-3.5 w-3.5" />;
    case "search_kb": return <BookOpen className="h-3.5 w-3.5" />;
    case "get_production_sequence": return <Factory className="h-3.5 w-3.5" />;
    default: return <Database className="h-3.5 w-3.5" />;
  }
}

function isChartOutput(output: unknown): output is { chart: ChartSpec } {
  return !!output && typeof output === "object" && "chart" in output;
}

// --- Tool calls display ---

interface ToolCall {
  tool: string;
  input: Record<string, unknown>;
  output: unknown;
}

function ToolCallGroup({ calls }: { calls: ToolCall[] }) {
  const [open, setOpen] = useState(false);

  const chartCalls = calls.filter((c) => isChartOutput(c.output));
  const otherCalls = calls.filter((c) => !isChartOutput(c.output));

  const totalMs = otherCalls.reduce((sum, c) => {
    const out = c.output as Record<string, unknown> | undefined;
    return sum + (out && typeof out.execution_ms === "number" ? out.execution_ms : 0);
  }, 0);

  const errorCount = otherCalls.filter((c) => {
    const out = c.output as Record<string, unknown> | undefined;
    return out && "error" in out;
  }).length;

  const durationLabel = totalMs > 0
    ? totalMs > 1000 ? `${(totalMs / 1000).toFixed(1)}s` : `${totalMs}ms`
    : null;

  return (
    <>
      {otherCalls.length > 0 && (
        <div className="my-1.5 rounded-md border border-border/50 bg-muted/30 text-xs">
          <button
            onClick={() => setOpen(!open)}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 transition-colors"
          >
            {open ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
            <Database className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">
              {otherCalls.length} tool call{otherCalls.length !== 1 ? "s" : ""}
              {durationLabel && ` (${durationLabel})`}
            </span>
            {errorCount > 0 && (
              <span className="ml-auto flex items-center gap-1 text-destructive">
                <AlertCircle className="h-3 w-3" /> {errorCount}
              </span>
            )}
          </button>

          {open && (
            <div className="border-t border-border/50 p-1">
              {otherCalls.map((call, i) => (
                <ToolCallBlock key={i} call={call} />
              ))}
            </div>
          )}
        </div>
      )}

      {chartCalls.map((call, i) => (
        <ChartBlock key={`chart-${i}`} spec={(call.output as { chart: ChartSpec }).chart} />
      ))}
    </>
  );
}

function ToolCallBlock({ call }: { call: ToolCall }) {
  const [open, setOpen] = useState(false);
  const output = call.output as Record<string, unknown> | undefined;
  const hasError = output && "error" in output;
  const rowCount = output && "row_count" in output ? (output.row_count as number) : null;
  const execMs = output && "execution_ms" in output ? (output.execution_ms as number) : null;

  let summary = call.tool;
  if (call.tool === "run_sql") {
    const query = (call.input.query as string) || "";
    summary = query.length > 80 ? query.slice(0, 80) + "..." : query;
  } else if (call.tool === "get_table_schema") {
    summary = `Schema: ${call.input.table_name}`;
  } else if (call.tool === "calculate") {
    summary = `Calculate: ${call.input.expression}`;
  } else if (call.tool === "list_tables") {
    summary = "List tables";
  } else if (call.tool === "create_chart") {
    summary = `Chart: ${(call.input.title as string) || "Visualisation"}`;
  }

  if (isChartOutput(call.output)) {
    return <ChartBlock spec={(call.output as { chart: ChartSpec }).chart} />;
  }

  return (
    <div className="my-1.5 rounded-md border border-border/50 bg-muted/30 text-xs">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
        {toolIcon(call.tool)}
        <span className="truncate font-mono text-muted-foreground">{summary}</span>
        <span className="ml-auto flex items-center gap-2 shrink-0">
          {hasError && <AlertCircle className="h-3 w-3 text-destructive" />}
          {rowCount !== null && <span className="text-muted-foreground">{rowCount} rows</span>}
          {execMs !== null && <span className="text-muted-foreground">{execMs}ms</span>}
        </span>
      </button>

      {open && (
        <div className="border-t border-border/50 p-3">
          {call.tool === "run_sql" && !!call.input.query && (
            <pre className="mb-2 whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs text-foreground">
              {String(call.input.query)}
            </pre>
          )}
          {hasError ? (
            <p className="text-destructive">{String(output!.error)}</p>
          ) : (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs text-muted-foreground">
              {JSON.stringify(call.output, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// --- Message bubble (read-only) ---

function SharedMessage({ msg }: { msg: SharedConvType["messages"][number] }) {
  const isUser = msg.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`rounded-lg px-4 py-3 ${
          isUser
            ? "max-w-[80%] bg-primary text-primary-foreground"
            : "max-w-[90%] bg-card text-card-foreground border border-border"
        }`}
      >
        {!isUser && msg.tool_calls && msg.tool_calls.length > 0 && (
          <div className="mb-2">
            <ToolCallGroup calls={msg.tool_calls} />
          </div>
        )}

        {isUser ? (
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</div>
        ) : (
          <div className="text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {msg.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main page ---

export function SharedConversation() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error } = useSharedConversation(token ?? null);

  useSetPageHeader({
    title: data?.title || "Shared Conversation",
    icon: Share2,
    actions: (
      <>
        <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
          Read-only
        </span>
        <a
          href="/app/analyst"
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <BrainCircuit className="h-4 w-4" />
          <span className="hidden sm:inline">Open Analyst</span>
        </a>
      </>
    ),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">
          {error ? "This share link is invalid or has expired." : "Conversation not found."}
        </p>
        <a
          href="/app/analyst"
          className="text-sm text-primary underline underline-offset-2 hover:text-primary/80"
        >
          Go to Data Analyst
        </a>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-3xl space-y-4">
          {data.messages.map((msg) => (
            <SharedMessage key={msg.id} msg={msg} />
          ))}
        </div>
      </div>
    </div>
  );
}
